import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { getDesktop } from '@/desktop/desktop';
import { desktopUpdateStore } from '@/desktop/desktop-update-state';

/**
 * Updates — the running version and a member-initiated update check.
 * The background check stays quiet on failure; a check the member asks
 * for always answers ("up to date", the offer dialog, or an honest
 * "couldn't check"). Desktop-only (the shell owns the update engine).
 */
export const UpdatesSection = observer(function UpdatesSection() {
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const version = desktopUpdateStore.state?.current;

  const handleCheck = async () => {
    const updates = getDesktop()?.updates;
    if (!updates?.check || isChecking) return;
    setIsChecking(true);
    setResult(null);
    try {
      const answer = await updates.check();
      setResult(
        answer.outcome === 'up-to-date'
          ? `You're up to date.`
          : answer.outcome === 'available'
            ? `Version ${answer.version} is available — see the download prompt.`
            : answer.outcome === 'unavailable'
              ? 'Updates run in the installed app.'
              : `Couldn't check for updates: ${answer.error}`,
      );
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div data-testid="update-section" className="space-y-2">
      <h3 className="text-sm font-semibold text-stone-700 dark:text-stone-300">Updates</h3>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-stone-500 dark:text-stone-400">
          SKJV Bible Study {version ?? ''}
        </span>
        <button
          data-testid="update-check"
          onClick={() => void handleCheck()}
          disabled={isChecking}
          className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm text-stone-700 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-800"
        >
          {isChecking ? 'Checking…' : 'Check for Updates'}
        </button>
      </div>
      {result && (
        <p data-testid="update-check-result" className="text-sm text-stone-500 dark:text-stone-400">
          {result}
        </p>
      )}
    </div>
  );
});
